import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { optionalEmailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { userService } from '@/services/user_service'
import { useAuth } from '@/hooks/useAuth'
import { useMe } from '@/hooks/useMe'
import { PasswordResetSection } from '@/components/PasswordResetSection'
import { toast } from 'sonner'
import { ClinicGroupUser } from '@magnetar/admin-api-interfaces'

const formSchema = z.object({
  account: z.string().min(1, { message: '請輸入使用者帳號' }),
  user_name: z.string().min(1, { message: '請輸入使用者名稱' }),
  email: optionalEmailSchema(),
})

type FormValues = z.infer<typeof formSchema>

interface EditClinicGroupUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicGroupUser: ClinicGroupUser
  onSuccess: () => void
}

export function EditClinicGroupUserDialog({
  open,
  onOpenChange,
  clinicGroupUser,
  onSuccess,
}: EditClinicGroupUserDialogProps) {
  const { token } = useAuth()
  const { user: currentUser } = useMe()
  const hasEmail = !!clinicGroupUser.user?.email
  const [pendingPassword, setPendingPassword] = useState<string | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // sys_admin 和 provider_admin 可以重設密碼
  const canResetPassword = currentUser?.role_type === 'sys_admin' || currentUser?.role_type === 'provider_admin'

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: clinicGroupUser.user?.account || '',
      user_name: clinicGroupUser.user?.user_name || '',
      email: clinicGroupUser.user?.email || '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        account: clinicGroupUser.user?.account || '',
        user_name: clinicGroupUser.user?.user_name || '',
        email: clinicGroupUser.user?.email || '',
      })
      setPendingPassword(undefined)
    }
  }, [open, clinicGroupUser, form])

  const onSubmit = async (values: FormValues) => {
    if (!token || !clinicGroupUser.user_id) return

    setIsSubmitting(true)
    try {
      const data: {
        user_name: string
        email?: string | null
        password?: string
      } = {
        user_name: values.user_name,
        email: hasEmail ? clinicGroupUser.user?.email : (values.email || null),
      }

      if (pendingPassword) {
        data.password = pendingPassword
      }

      const result = await userService.updateUser(clinicGroupUser.user_id, data, token)

      if (result.success) {
        toast.success('更新使用者成功')
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error(result.error?.message || '更新使用者失敗')
      }
    } catch {
      toast.error('更新使用者失敗')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>編輯使用者</DialogTitle>
          <DialogDescription>編輯集團使用者基本資料</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者帳號" disabled {...field} />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">帳號無法修改</p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="example@domain.com"
                      disabled={hasEmail}
                      {...field}
                    />
                  </FormControl>
                  {hasEmail && (
                    <p className="text-xs text-muted-foreground">Email 已設定，無法修改</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <PasswordResetSection
              canResetPassword={canResetPassword}
              onPasswordChange={setPendingPassword}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? '處理中...' : '儲存'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
